import React, { ChangeEvent, useCallback, useState, useMemo, useRef, useEffect } from "react";
import styles from "./EditContent.module.scss";
import {
  TextInput,
  TextInputValue,
} from "../../../../../components/text-input/TextInput";
import Button from "../../../../../components/button/Button";
import { ContentType, ExtendedSllModule } from "../../../../../types";
import { uploadFile, getMediaAbsolutePath } from "../../../../../services/File";
import { LazyQueryExecFunction, MutationFunction } from "@apollo/client";
import ContentAudio from "../../../../../components/content-audio/ContentAudio";
import ContentPDF from "../../../../../components/content-pdf/ContentPDF";
import ContentSlide from "../../../../../components/content-slide/ContentSlide";
import FeedbackFormEditor from "./feedback-form-editor/FeedbackFormEditor";
import VideoForm from "@components/video-form/VideoForm";
import {
  Exact,
  GetUrlQuery,
  GetUrlQueryVariables,
  SllDeleteGroupModuleContentMutation,
  SllDeleteGroupModuleMutation,
  SllDeleteModuleContentMutation,
  useGetZipSlideUrlLazyQuery,
  useUploadZipSlideMutation,
  Video,
} from "@api/graphql-codegen";
import { ReloadOutlined } from "@ant-design/icons";
import Checkbox from "../../../../../components/checkbox/Checkbox";
import { ErrorKeys } from "./useEditContent";
import Spinner from "@components/spinner/Spinner";
import CodeEditor from "@src/components/code-editor/CodeEditor";
import RevealSlide from "@src/components/reveal-slide/RevealSlide";
import { useAWSKeys } from "@src/hooks";
import PreviewIcon from "src/assets/icons/preview.svg?react";
import { useToast } from "@src/hooks";
import Skeleton from "react-loading-skeleton";
import RadioGroup from "@src/components/radio-group/RadioGroup";
import { isFilePath, isValidUrl } from "@src/utils/helpers";
import { logger } from '../../../../../utils/logger';

interface SlideMessage {
  type: string;
  totalSlides?: number;
  currentPage?: number;
  page?: number;
  subPage?: number;
}

type Props = {
  content?: ExtendedSllModule;
  formData?: string;
  errors: Partial<Record<ErrorKeys, string>>;
  setErrors: (e: Partial<Record<ErrorKeys, string>>) => void;
  fileLoading?: boolean;
  getUrl: LazyQueryExecFunction<GetUrlQuery, GetUrlQueryVariables>;
  deleteGroupModuleData: MutationFunction<
    SllDeleteGroupModuleMutation,
    Exact<{
      input: {
        moduleId: string;
        groupId: string;
      };
    }>
  >;
  deleteSLLGroup: MutationFunction<
    SllDeleteGroupModuleMutation,
    Exact<{
      input: {
        moduleId: string;
        groupId: string;
      };
    }>
  >;
  deleteSLLGroupContent: MutationFunction<
    SllDeleteGroupModuleContentMutation,
    Exact<{
      input: {
        moduleId: string;
        groupId: string;
      };
    }>
  >;
  deleteModuleData: MutationFunction<
    SllDeleteModuleContentMutation,
    Exact<{
      input: { id: string };
    }>
  >;
  fileUploadingPercent: number;
  setFileUploadingPercent: (x: number) => void;
  clearFile: () => void;
  handleRemoveGroup: () => void;
  description: string;
  setDescription: (description: string) => void;
  data: string | null;
  setData: (d: string | null) => void;
  dataInputRef: React.RefObject<HTMLInputElement>;
  isDefault: boolean;
  uploadVideoAndSave: (
    file: File,
    data: { url: string; uploadId: string }
  ) => void;
  handleChangeVideo: (video: Video) => void;
  loading?: boolean;
  isGroupRemoving?: boolean;
  isDisabled?: boolean;
  handleContentDataSave: (data: string) => void;
  onChangeRequiredLesson: (v: boolean) => void;
  onChangeLastLessonMark: (v: boolean) => void;
  onChangeAdditionalAfter: (v: boolean) => void;
  isRequired: boolean;
  isLastLesson: boolean;
  isAdditionalAfter: boolean;
  isClassPresentation: boolean;
  fileUploadFinished: boolean;
  isPreparation?: boolean;
};

const exitTicketTypeOptions = [
  {label: "PDF Slide", value: "1"},
  {label: "Slides.com URL", value: "2"},
]

function GenericContent({
  handleRemoveGroup,
  content,
  formData,
  errors,
  setErrors,
  fileLoading,
  getUrl,
  fileUploadingPercent,
  uploadVideoAndSave,
  isDisabled,
  setFileUploadingPercent,
  clearFile,
  description,
  setDescription,
  data,
  setData,
  dataInputRef,
  isDefault,
  loading: overallLoading,
  deleteModuleData,
  deleteGroupModuleData,
  deleteSLLGroup,
  deleteSLLGroupContent,
  isGroupRemoving,
  handleContentDataSave,
  onChangeRequiredLesson,
  onChangeLastLessonMark,
  isRequired,
  isLastLesson,
  onChangeAdditionalAfter,
  isAdditionalAfter,
  isClassPresentation,
  fileUploadFinished,
  handleChangeVideo,
}: Props) {
  const [loading, setLoading] = useState(false);
  const { awsKeys } = useAWSKeys();
  const { successToast, errorToast } = useToast();
  const isMounted = useRef(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [exitTicketSlideType, setExitTicketSlideType] = useState('1');

  useEffect(() => {
    isMounted.current = true; // Component is mounted
    return () => {
      isMounted.current = false; // Component is unmounted
    };
  }, []);

  useEffect(() => {
    // Function to handle messages from iframe
    const handleMessage = (event: MessageEvent<SlideMessage>) => {
      if (event.data.type === "SLIDE_INFO") {
        setCurrentPage(event.data.subPage || 1);
        setTotalPage(event.data.totalSlides || 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setCurrentPage, setTotalPage]);

  const [uploadZIPSlide, { loading: zipUploading }] = useUploadZipSlideMutation({
    onError(error) {
      logger.error(error.message);
      errorToast(error.message);
    },
  });
  const [getZIPSlideUrl, { loading: getSlideURLoading }] = useGetZipSlideUrlLazyQuery({
    fetchPolicy: 'network-only',
    onError(error) {
      logger.error(error.message);
      errorToast(error.message);
    },
  })

  const [url, setUrl] = useState("");

  const onUpload = useCallback(async() => {
    if (!!url) {
      setLoading(true);
      
      // Store the current URL to compare against later
      const currentSlideUrl = data || content?.data;
      
      try {
        const response = await uploadZIPSlide({
          variables: {
            input: {
              groupId: content?.groupId,
              id: content?.id || '',
              isCourse: false,
              url: url
            }
          }
        });
        
        if (response.data?.uploadZIPSlide) {
          successToast('Zip file uploaded successfully. Processing slides (this may take up to 2 minutes)...');
          
          const fetchSlideUrl = async (retryCount = 0, maxRetries = 24) => { // Increased to 24 retries (6 minutes total)
            if (!isMounted.current) {
              setLoading(false);
              return;
            }
            
            try {
              const res = await getZIPSlideUrl({
                variables: { 
                  groupId: content?.groupId,
                  id: content?.id || '',
                  isCourse: false,
                }
              });
    
              const newSlideUrl = res.data?.getZIPSlideUrl;
              
              // Always accept the new URL if it's valid, even if it's the same as before
              // This ensures updates to the same slides.com presentation are processed
              if (newSlideUrl && newSlideUrl.trim() !== '') {
                setLoading(false);
                setData(newSlideUrl);
                handleContentDataSave(newSlideUrl);
                setUrl(''); // Clear the input field after successful upload
                successToast('Slides processed and saved successfully!');
                
                // Force iframe refresh by adding a timestamp to the URL
                if (iframeRef.current) {
                  const timestamp = Date.now();
                  const currentSrc = iframeRef.current.src;
                  const newSrc = currentSrc.includes('?') 
                    ? currentSrc.split('?')[0] + `?t=${timestamp}` 
                    : `${currentSrc}?t=${timestamp}`;
                  iframeRef.current.src = newSrc;
                }
              } else if (retryCount < maxRetries) {
                const remainingTime = Math.ceil((maxRetries - retryCount) * 15 / 60);
                logger.info(`URL not updated yet, retrying... (${retryCount + 1}/${maxRetries}) - ${remainingTime} minutes remaining`);
                setTimeout(() => {
                  if (isMounted.current) {
                    fetchSlideUrl(retryCount + 1, maxRetries);
                  }
                }, 15000); // Wait 15 seconds before retrying
              } else {
                setLoading(false);
                errorToast('Slide processing is taking longer than expected. The slides may still be processing in the background. Please check back in a few minutes.');
              }
            } catch (error) {
              logger.error('Error fetching slide URL:', error);
              if (retryCount < maxRetries) {
                setTimeout(() => {
                  if (isMounted.current) {
                    fetchSlideUrl(retryCount + 1, maxRetries);
                  }
                }, 15000);
              } else {
                setLoading(false);
                errorToast('Failed to process slides. Please try again.');
              }
            }
          };
          
          // Start fetching after initial delay
          setTimeout(() => {
            if (isMounted.current) {
              fetchSlideUrl();
            }
          }, 15000); // Increased initial delay to 15 seconds
        } else {
          setLoading(false);
          errorToast('Failed to upload zip file. Please try again.');
        }
      } catch (error) {
        setLoading(false);
        logger.error('Upload error:', error);
        errorToast('Failed to upload zip file. Please check the URL and try again.');
      }
    }
  }, [url, content, data, successToast, errorToast, uploadZIPSlide, getZIPSlideUrl, handleContentDataSave, setData]);

  const handleSetSlideFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e?.target?.files?.[0];
      if (file) {
        try {
          setLoading(true);
          const location = await uploadFile(awsKeys, file, "content", (percent) => {
            setFileUploadingPercent(percent);
          });
          handleContentDataSave(location);
          setData(location);
        } catch (e) {
          alert("Something went wrong");
        } finally {
          setLoading(false);
        }
      }
    },
    [awsKeys, handleContentDataSave, setData, setFileUploadingPercent]
  );

  const handleSetVideoFile = useCallback(
    async (file?: File) => {
      if (file) {
        const videoUrlUploadData = await getUrl({variables: {input: {metadata: file.name}}});
        if (
          videoUrlUploadData.data?.videoUpload?.url &&
          videoUrlUploadData.data?.videoUpload?.uploadId
        ) {
          uploadVideoAndSave(file, {
            url: videoUrlUploadData.data?.videoUpload?.url,
            uploadId: videoUrlUploadData.data?.videoUpload?.uploadId,
          });
        }
      } else {
        clearFile();
      }
    },
    [clearFile, getUrl, uploadVideoAndSave]
  );

  const handleSetVideoLink = useCallback(async (video) => {
    handleChangeVideo(video);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeText = useCallback(
    (setter, key?: ErrorKeys) => (value: TextInputValue) => {
      setter(value);
      if (key) {
        setErrors({
          ...errors,
          [key]: "",
        });
      }
    },
    [errors, setErrors]
  );

  const handlePreviewPage = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "PREV_SLIDE" }, "*");
    }
  }, []);

  const handleNextPage = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "NEXT_SLIDE" }, "*");
    }
  }, []);

  const handleSlideType = (value: string) => {
    setExitTicketSlideType(value);
  }

  const contentType = useMemo(() => content?.type, [content?.type]);
  const isSkill = content && content.type === ContentType.SKILL;
  // Use local data state if available, otherwise fall back to content data
  const currentData = data || content?.data;
  
  // Ensure the iframe source URL is properly formatted
  const iframeSrc = useMemo(() => {
    if (!currentData) return '';
    
    // If the URL already ends with index.html, use it as is
    if (currentData.endsWith('index.html')) return currentData;
    
    // If the URL ends with a slash, append index.html
    if (currentData.endsWith('/')) return `${currentData}index.html`;
    
    // Otherwise, append /index.html
    return `${currentData}/index.html`;
  }, [currentData]);

  const genericContent = useCallback(() => {
    switch (contentType) {
      case ContentType.PDF: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"application/pdf"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentPDF data={getMediaAbsolutePath(data)} />
              </div>
            )}
          </>
        );
      }
      case ContentType.SLIDE: {
        return (
          <>
            {data ? (
              <>
                <div className={`${styles.row} ${styles.preview}`}>
                  <ContentSlide data={getMediaAbsolutePath(data)} />
                </div>
                <div className={`${styles.row} ${styles.rowBottomRight}`}>
                  <Button
                    outline
                    disabled={loading}
                    label={
                      loading ? `Uploading ${fileUploadingPercent}%` : "Replace"
                    }
                    size={"extraSmall"}
                    onClick={() => setData("")}
                    className={styles.replaceBtn}
                    leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                  />
                </div>
              </>
            ) : (
              <div className={`${styles.row} ${styles.rowBottom}`}>
                <input
                  type="file"
                  accept={"application/pdf"}
                  onChange={handleSetSlideFile}
                  ref={dataInputRef}
                />
                {loading ? (
                  <div>{`Uploading ${fileUploadingPercent}%`}</div>
                ) : null}
              </div>
            )}
          </>
        );
      }
      case ContentType.ZIPSLIDE: {
        // Check if there's an existing URL in the database
        const existingUrl = data || content?.data;
        const hasExistingUrl = existingUrl && existingUrl.trim() !== '';
        
        return (
          <div className={styles.flex}>
            <div className={styles.newSlideUpload}>
              <TextInput
                label={""}
                id={"upload-input"}
                placeholder="Paste Zip Slides.com URL"
                value={url}
                disabled={zipUploading || getSlideURLoading || loading}
                onChange={(value: TextInputValue) => {
                  setUrl(`${value}`);
                }}
                className={styles.revealURLEditor}
              />
              <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                <Button
                  label="Add new URL"
                  className={styles.uploadBtn}
                  onClick={onUpload}
                  disabled={url === '' || zipUploading || getSlideURLoading || loading}
                  leftIcon={zipUploading || getSlideURLoading ? <Spinner width={20} height={20} /> : <ReloadOutlined style={{ marginRight: 8 }} />}
                />
              </div>
              {hasExistingUrl && (
                <div>
                  <Button
                    label="Refresh Slide"
                    className={styles.refreshBtn}
                    onClick={() => {
                      // Convert CDN URL back to slides.com URL format
                      const cdnUrl = existingUrl.split('?')[0]; // Remove any query parameters
                      const pathParts = cdnUrl.split('/');
                      const slidePath = pathParts[pathParts.length - 1];
                      
                      // Construct the likely original slides.com URL with /edit suffix
                      const slidesUrl = `https://slides.com/openparachute/${slidePath}/edit`;
                      
                      // Set the URL to the original slides.com URL and trigger upload
                      setUrl(slidesUrl);
                      setTimeout(() => onUpload(), 0);
                    }}
                    disabled={zipUploading || getSlideURLoading || loading}
                    leftIcon={zipUploading || getSlideURLoading ? <Spinner width={20} height={20} /> : <ReloadOutlined style={{ marginRight: 8 }} />}
                  />
                </div>
              )}
            </div>
            <div className={styles.previewContainer}>
            {loading ? <div className={styles.flex}>
              <label className={styles.label}>Preview</label>
              <Skeleton height={15} width={960}/>
            </div> :
            (isValidUrl(iframeSrc) && 
              <div className={styles.row}>
                <div className={styles.flex}>
                  <label className={styles.label}>Preview</label>
                  <div
                    className={`${styles.row} ${styles.newSlidePreview}`}
                  >
                    <iframe
                      ref={iframeRef}
                      src={iframeSrc}
                      width="960px"
                      height="540px"
                      style={{ border: 'none' }}
                      title="Slide"
                    />
                    <div className={styles.flexJustifyBetween}>
                      <Button disabled={currentPage === 1} label="Previous" size="small" className={styles.slideButton} onClick={handlePreviewPage}/>
                      <span className={styles.pages}>{`${currentPage}/${totalPage}`}</span>
                      <Button disabled={currentPage === totalPage} label="Next" size="small" className={styles.slideButton} onClick={handleNextPage}/>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        );
      }
      case ContentType.REVEALSLIDE: {
        return (
          <div className={styles.flexRow}>
            <div className={styles.row}>
              <CodeEditor
                onSubmit={(data) => {
                  setData(data);
                  handleContentDataSave(data);
                }}
                onSetData={setData}
                onSetUrl={false}
                lessonType
                data={data}
                url={description}
              />
            </div>
            <div className={styles.previewContainer}>
              {data ? (
                <div className={styles.row}>
                  <div className={styles.flex}>
                    <label className={styles.label}>Preview</label>
                    <div
                      className={`${styles.row} ${styles.revealSlidePreview}`}
                    >
                      <RevealSlide data={data} key={data} controls />
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`${styles.row} ${styles.rowBottom}`}>
                  <div className={styles.row}>
                    <div className={styles.flex}>
                      <label className={styles.label}>Preview</label>
                      <div className={styles.revealSlidePreview}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }
      case ContentType.SKILL: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"audio/*"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentAudio data={data} />
              </div>
            )}
          </>
        );
      }
      case ContentType.AUDIO: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input
                type="file"
                accept={"audio/*"}
                onChange={handleSetSlideFile}
                ref={dataInputRef}
              />
              {loading ? (
                <div>{`Uploading ${fileUploadingPercent}%`}</div>
              ) : null}
            </div>
            {data && (
              <div className={`${styles.row} ${styles.preview}`}>
                <ContentAudio data={data} />
              </div>
            )}
          </>
        );
      }
      case ContentType.VIDEO: {
        return (
          <VideoForm
            data={data || undefined}
            meta={content!.meta}
            onChange={handleSetVideoFile}
            onChangeVideo={handleSetVideoLink}
            isUploading={(fileLoading || overallLoading) && !isGroupRemoving}
            uploadingPercent={fileUploadingPercent}
            isDisabled={isDisabled}
            fileUploadFinished={fileUploadFinished}
          />
        );
      }
      case ContentType.FORM: {
        return (
          <>
            <div className={styles.preExitTicketTitle}>
              Pre-Exit Ticket Slide
            </div>
            {data ? (isFilePath(data) ? // data is PDF file path
              <>
                <div className={`${styles.row} ${styles.preview}`}>
                  <ContentSlide data={getMediaAbsolutePath(data)} />
                </div>
                <div className={`${styles.row} ${styles.rowBottomRight}`}>
                  <Button
                    outline
                    disabled={loading}
                    label={
                      loading ? `Uploading ${fileUploadingPercent}%` : "Replace"
                    }
                    size={"extraSmall"}
                    onClick={() => setData("")}
                    className={styles.replaceBtn}
                    leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                  />
                </div>
              </> : <>
                <div className={styles.row}>
                  <div className={styles.flex}>
                    <label className={styles.label}>Preview</label>
                    <div
                      className={`${styles.row} ${styles.newSlidePreview}`}
                    >
                      <iframe
                        ref={iframeRef}
                        src={iframeSrc}
                        width="960px"
                        height="540px"
                        style={{ border: 'none' }}
                        title="Slide"
                      />
                      <div className={styles.flexJustifyBetween}>
                        <Button disabled={currentPage === 1} label="Previous" size="small" className={styles.slideButton} onClick={handlePreviewPage}/>
                        <span className={styles.pages}>{`${currentPage}/${totalPage}`}</span>
                        <Button disabled={currentPage === totalPage} label="Next" size="small" className={styles.slideButton} onClick={handleNextPage}/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${styles.row} ${styles.rowBottomRight}`}>
                  <Button
                    outline
                    disabled={loading}
                    label="Replace"
                    size={"extraSmall"}
                    onClick={() => setData("")}
                    className={styles.replaceBtn}
                    leftIcon={<ReloadOutlined style={{ marginRight: 6 }} />}
                  />
                </div>
              </>
            ) : (<>
              <div style={{marginBottom: 20}}>
                <RadioGroup
                  value={exitTicketSlideType}
                  options={exitTicketTypeOptions}
                  onChange={handleSlideType}
                  name={''}
                  hasError={false}
                  subTitle={""}
                />
              </div>
              {exitTicketSlideType === exitTicketTypeOptions[0].value &&
              <div className={`${styles.row} ${styles.rowBottom}`}>
                <input
                  type="file"
                  accept={"application/pdf"}
                  onChange={handleSetSlideFile}
                  ref={dataInputRef}
                />
                {loading ? (
                  <div>{`Uploading ${fileUploadingPercent}%`}</div>
                ) : null}
              </div>  
              }
              {exitTicketSlideType === exitTicketTypeOptions[1].value && <div className={styles.flexRow}>
                <div className={styles.newSlideUpload}>
                  <TextInput
                    label={""}
                    id={"upload-input"}
                    placeholder="Paste Zip Slides.com URL"
                    value={url}
                    disabled={zipUploading || getSlideURLoading || loading}
                    onChange={(value: TextInputValue) => {
                      setUrl(`${value}`);
                    }}
                    className={styles.revealURLEditor}
                  />
                  <Button
                    label=""
                    className={styles.uploadBtn}
                    onClick={onUpload}
                    disabled={zipUploading || getSlideURLoading || loading}
                    leftIcon={zipUploading || getSlideURLoading ? <Spinner width={20} height={20} /> : <PreviewIcon />}
                  />
                </div>
                {loading ? <div className={styles.flex}>
                  <label className={styles.label}>Preview</label>
                  <Skeleton height={15} width={960}/>
                </div> : null}
              </div>
              }
              </>
            )}
            <div className={styles.divider} />
            <FeedbackFormEditor
              showError={!!errors.feedbackForm}
              formData={formData}
            />
          </>
        );
      }
      default: {
        return null;
      }
    }
  }, [
    content,
    contentType,
    data,
    dataInputRef,
    errors.feedbackForm,
    isDisabled,
    fileLoading,
    isGroupRemoving,
    fileUploadingPercent,
    formData,
    handleSetSlideFile,
    handleSetVideoFile,
    handleSetVideoLink,
    loading,
    overallLoading,
    setData,
    fileUploadFinished,
    handleContentDataSave,
    description,
    onUpload,
    url,
    zipUploading,
    getSlideURLoading,
    iframeSrc,
    currentPage,
    totalPage,
    handlePreviewPage,
    handleNextPage,
    exitTicketSlideType,
  ]);

  const handleRemoveModuleData = useCallback(() => {
    if (isDefault) {
      if (content?.id) {
        deleteModuleData({ variables: { input: { id: content?.id } } });
      }
    } else {
      if (content?.id && content?.groupId) {
        deleteSLLGroupContent({
          variables: {
            input: { moduleId: content?.id, groupId: content?.groupId },
          },
        });
      }
    }
  }, [
    content?.groupId,
    content?.id,
    deleteSLLGroupContent,
    deleteModuleData,
    isDefault,
  ]);

  const handleRemoveSLLGroup = useCallback(async () => {
    if (
      window.confirm(
        `Are you sure you want to remove ${content?.groupName} group in the Module?`
      )
    ) {
      if (content?.id && content.groupId) {
        await deleteSLLGroup({
          variables: {
            input: {
              moduleId: content?.id,
              groupId: content?.groupId,
            },
          },
        });
      }
    }
  }, [content?.groupId, content?.id, content?.groupName, deleteSLLGroup]);

  return (
    <>
      {!isDefault ? (
        <div className={`${styles.row} ${styles.rightRow}`}>
          <Button
            color="red"
            size="small"
            disabled={isDisabled}
            isLoading={isGroupRemoving}
            label={"Remove Group"}
            className={styles.removeGroupBtn}
            onClick={handleRemoveSLLGroup}
          />
        </div>
      ) : (
        <div>&nbsp;</div>
      )}
      {isSkill && (
        <div className={styles.row}>
          <TextInput
            textArea={true}
            label={"Description"}
            value={description === "Content description" ? "" : description}
            id={"description"}
            placeholder="Description"
            onChange={handleChangeText(setDescription)}
          />
        </div>
      )}
      {/* <div className={styles.row}>
        <TextInput hasError={!!errors.position} subTitle={errors.position} type={'number'} label={'Position'}
                  id={'position'} value={position} onChange={handleChangeText(setPosition, 'position')}/>
      </div> */}
      {/* {!isParent && !isSkill &&
      <div className={styles.row}>
        <SelectMulti label={'Groups'} options={groupOptions} onChange={handleChangeGroups} value={groups}/>
      </div>
      }
      {isFormType && !isParent && !isSkill &&
      <div className={styles.row}>
        <Checkbox id={'pauseHere'} checked={pauseHere} onChange={handleChangePauseHere} label={'Pause here'}/>
      </div>
      } */}
      {genericContent()}
      {data && contentType !== ContentType.FORM ? (
        <div className={styles.buttonContainer}>
          <div>
            <Button
              color="red"
              onClick={handleRemoveModuleData}
              outline={true}
              size={"small"}
              disabled={isDisabled || isGroupRemoving}
              leftIcon={
                isGroupRemoving ? <Spinner width={20} height={20} /> : undefined
              }
              label={"DELETE CONTENT"}
            />
          </div>
        </div>
      ) : null}
      {isDefault && isClassPresentation ? (
        <div className={`${styles.lastRow} ${styles.marginTop}`}>
          <Checkbox
            id={"required"}
            checked={isRequired}
            onChange={onChangeRequiredLesson}
            label={"Required Lesson Material"}
          />
          <Checkbox
            id={"isLastLesson"}
            checked={isLastLesson}
            onChange={onChangeLastLessonMark}
            label={"Mark lesson completed by this module"}
          />
          {contentType !== ContentType.FORM && (
            <Checkbox
              id={"isAdditionalAfter"}
              checked={isAdditionalAfter}
              onChange={onChangeAdditionalAfter}
              label={
                "This is the final exercise for this Lesson. The next Slides will be Additional Content if the Teacher has time."
              }
            />
          )}
        </div>
      ) : null}
    </>
  );
}

export default GenericContent;
